import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Columns, Download, Search } from 'lucide-preact'
import { useState, useMemo, useEffect, useId } from 'preact/hooks'
import { useTableSort, sortData, type SortDirection } from '@/hooks/useTableSort'
import { ColumnSelectorModal, type ColumnSelectorItem } from '@/components/modals/ColumnSelectorModal'
import { BRAND_CLASSES } from '@/constants/theme'

/**
 * The card. One constant for all four render branches — they had already drifted once, so that a
 * loading grid overflowed its header strip sideways while a populated one clipped the same strip's
 * last button.
 */
const CARD_CLASS =
  'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden'

/** Export and Columns. Focus ring included — they shipped without one, on every grid in the app. */
const TOOLBAR_BUTTON =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-800'

export interface ColumnConfig<T> {
  /** Unique key for the column */
  key: string
  /** Display label for column header */
  header: string
  /** Whether this column is sortable */
  sortable?: boolean
  /** Custom width for the column (CSS value) */
  width?: string
  /** Custom renderer for cell content */
  render?: (item: T, index: number) => preact.ComponentChild
  /**
   * Raw value behind the cell. Two jobs, both of them real:
   *
   * 1. It is what a `sortable` column sorts by. A column with an accessor needs no comparator from the
   *    caller — the grid builds one (nulls last, `localeCompare` for strings, numerically for numbers).
   * 2. It renders the cell when there is no `render`.
   *
   * Give every sortable column either an accessor or a `getSortComparator` case, or the header will not
   * offer sorting at all.
   */
  accessor?: (item: T) => string | number | boolean | Date | null | undefined
  /** Alignment for cell content */
  align?: 'left' | 'center' | 'right'
  /** Custom class name for cell */
  className?: string
  /** Responsive visibility classes (e.g., "hidden md:table-cell" for hide on mobile, show on desktop) */
  hidden?: string
  /** Column can't be hidden when column visibility is enabled (always visible) */
  required?: boolean
  /** Whether visible by default when column visibility is enabled. Default: true */
  defaultVisible?: boolean
}

export interface ExportColumnDef<T> {
  /** Header label in the CSV */
  header: string
  /** Extract the value for this column from a data item */
  getValue: (item: T) => string | number | boolean | null | undefined
}

export interface DataGridExportConfig<T> {
  /** Function to generate the filename (called at export time, should include .csv extension) */
  getFilename: () => string
  /** Column definitions for the CSV export */
  columns: ExportColumnDef<T>[]
}

export interface DataGridProps<T> {
  /** Array of data items to display */
  data: T[]
  /** Column configuration */
  columns: ColumnConfig<T>[]
  /** Unique key function to identify rows */
  getRowKey: (item: T) => string | number
  /** Whether pagination is enabled */
  pagination?: boolean
  /** Number of items per page (default: 10) */
  pageSize?: number
  /** Loading state */
  isLoading?: boolean
  /** Empty state message */
  emptyMessage?: string
  /** Click handler for rows */
  onRowClick?: (item: T) => void
  /**
   * Sort order the raw value cannot express — a lifecycle rank, "never delivered sorts below zero",
   * an urgency ordering.
   *
   * Return a comparator for the keys you own and `undefined` for the rest, and those fall through to
   * their column's accessor. Returning a comparator for every key — the `default: return 0` shape —
   * shadows the accessor everywhere and puts a live sort arrow over a column that never moves, which
   * is the bug this ticket removed.
   *
   * Most grids need none: mark the column `sortable`, give it an `accessor`, and the grid sorts it.
   */
  getSortComparator?: (key: string) => ((a: T, b: T) => number) | undefined
  /** Default sort key */
  defaultSortKey?: string
  /** Default sort direction */
  defaultSortDirection?: SortDirection
  /** When true, columns maintain natural widths and the table scrolls horizontally instead of squishing */
  scrollable?: boolean
  /** localStorage key for column visibility. When set, enables column visibility toggle with persistence */
  columnVisibilityKey?: string
  /** Export configuration. When set, enables an Export button in the toolbar that generates a CSV download */
  exportConfig?: DataGridExportConfig<T>
  /**
   * Whatever the caller narrowed `data` by — a search query, a filter selection. Changing it sends
   * the reader back to the first page.
   *
   * Row count alone cannot decide this: two different queries can match the same number of rows,
   * and holding page 3 across that change shows results whose first page nobody has seen. Only the
   * caller knows the set was rebuilt rather than merely changed size.
   */
  pageResetKey?: string | number
  /**
   * Grid name, top-left of the header strip — the plain plural noun for the rows, in the product's own
   * word for them: "Contacts", "Companies", "Webs", "Deployments".
   *
   * Required (ORB-715), because it is the one thing about a grid the grid cannot work out for itself.
   * Everything else in the strip is derived. Leave out the scope the page already supplies ("Campaign
   * Contacts"), the view mode ("Web List"), the filter ("Active Users") and the category suffix
   * ("Deployment History") — the page around the grid says those, and saying them twice is the drift
   * this prop exists to end.
   *
   * The conversion deletes the heading it replaces. A tab or a switch whose selected segment happens to
   * read the same word is a control, not a second label, and stays.
   */
  title: preact.ComponentChild
  /**
   * Muted note beside the title, for a scoping rule the noun cannot carry and a reader would otherwise
   * get wrong — "Only people this workspace targeted or heard from."
   *
   * Not for freshness stamps, filter state or row counts. If it restates the title, leave it out.
   */
  subtitle?: preact.ComponentChild
  /**
   * Override for the "N results" figure, for a grid whose `data` is one server page of a larger set.
   *
   * There are none today, so no caller passes this: the grid counts the rows it was handed, which is the
   * only number that can never contradict what is on screen. Pass it only when `data.length` would be a
   * page size rather than a total.
   */
  resultCount?: number
  /**
   * Caller-owned toolbar content — a search box, a refresh, an export the grid cannot build itself.
   * Sits right-aligned, before the built-in Export and Columns buttons.
   *
   * A search input passed here must be declared at module scope, not inside the calling component's
   * body: a component redefined each render remounts, and the box loses focus on every keystroke.
   */
  toolbarActions?: preact.ComponentChild
  /**
   * Toolbar search box. The caller owns the value and decides whether filtering happens in the browser
   * or on the server, so this is presentation only — debounce and page-reset stay with the caller,
   * which is the only side that knows which it is doing.
   */
  search?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    ariaLabel?: string
  }
}

/** Escape a CSV value (wrap in quotes if it contains commas, quotes, or newlines) */
function escapeCsvValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/** Generate CSV string from data and export column definitions */
function generateCsv<T>(data: T[], columns: ExportColumnDef<T>[]): string {
  const headers = columns.map(col => col.header)
  const rows = data.map(item =>
    columns.map(col => escapeCsvValue(col.getValue(item)))
  )
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
}

/** Trigger a browser download of CSV content */
function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.setAttribute('rel', 'noopener noreferrer')
  link.setAttribute('target', '_self')
  link.dispatchEvent(new MouseEvent('click', { view: window, bubbles: false, cancelable: true }))
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

type RawValue = string | number | boolean | Date | null | undefined

/**
 * Order two raw `accessor` values.
 *
 * Absent values (null/undefined) sort LAST in both directions. That takes a wrinkle: `sortData` builds
 * a descending order by reversing the ascending one, so absent has to be pushed to the front of the
 * ascending pass for a descending sort — hence `missingRank`. Without it, sorting a Launch Date column
 * newest-first opens on a block of unscheduled rows, which is the one thing that column is never asked.
 *
 * An accessor that returns `''` for a missing value is returning a real empty string, and it sorts with
 * the strings. Return null if you want it treated as absent.
 */
function compareRawValues(a: RawValue, b: RawValue, missingRank: number): number {
  const aMissing = a === null || a === undefined
  const bMissing = b === null || b === undefined
  if (aMissing || bMissing) return aMissing === bMissing ? 0 : aMissing ? missingRank : -missingRank

  if (a instanceof Date || b instanceof Date) {
    return Number(a instanceof Date ? a.getTime() : a) - Number(b instanceof Date ? b.getTime() : b)
  }
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)

  // localeCompare, not `<`/`>`, so "apple" sorts before "Zebra" rather than after it.
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/** Hook for managing column visibility state with localStorage persistence */
function useColumnVisibility<T>(
  columns: ColumnConfig<T>[],
  storageKey: string | undefined
) {
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(() => {
    if (!storageKey) return {}
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) return JSON.parse(stored) as Record<string, boolean>
    } catch {
      // ignore parse errors
    }
    return {}
  })

  const isVisible = (key: string): boolean => {
    if (key in visibilityMap) return visibilityMap[key]
    const col = columns.find(c => c.key === key)
    return col?.defaultVisible !== false
  }

  const toggleColumn = (key: string) => {
    const col = columns.find(c => c.key === key)
    if (col?.required) return
    const next = { ...visibilityMap, [key]: !isVisible(key) }
    setVisibilityMap(next)
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* ignore */ }
    }
  }

  const resetToDefaults = () => {
    setVisibilityMap({})
    if (storageKey) {
      try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
    }
  }

  const visibleColumns = storageKey
    ? columns.filter(col => isVisible(col.key))
    : columns

  const selectorItems: ColumnSelectorItem[] = columns.map(col => ({
    id: col.key,
    label: col.header,
    visible: isVisible(col.key),
    required: col.required,
  }))

  return { visibleColumns, selectorItems, toggleColumn, resetToDefaults }
}

export function DataGrid<T>({
  data,
  columns,
  getRowKey,
  pagination = false,
  pageSize = 10,
  isLoading = false,
  emptyMessage = 'No data available',
  onRowClick,
  getSortComparator,
  defaultSortKey,
  defaultSortDirection = 'asc',
  scrollable = false,
  columnVisibilityKey,
  exportConfig,
  pageResetKey,
  title,
  subtitle,
  resultCount,
  toolbarActions,
  search,
}: DataGridProps<T>) {
  const [page, setPage] = useState(1)
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)
  // Names the <table> for a screen reader. Now that every grid has a title, every table can say which
  // one it is — the payoff for making `title` required.
  const titleId = useId()
  const { sortConfig, requestSort, getSortIndicator } = useTableSort<string>(
    defaultSortKey,
    defaultSortDirection
  )
  const { visibleColumns, selectorItems, toggleColumn, resetToDefaults } = useColumnVisibility(columns, columnVisibilityKey)

  // The comparator for a column key, or undefined when the grid genuinely cannot sort it.
  //
  // A caller's `getSortComparator` wins where it has an opinion — a lifecycle rank, an urgency order,
  // "never delivered sorts below zero" — and the column's own `accessor` covers the rest. Deriving from
  // the accessor is what makes `sortable` mean something on its own: before ORB-715 a column could be
  // marked sortable, draw an arrow, and reorder nothing, because sorting silently required a second
  // prop nobody could see from the column definition.
  //
  // Memoised on the props themselves, so the five callers that define their comparator inline stop
  // re-sorting the whole list on every render.
  const comparatorFor = useMemo(() => {
    const byKey = new Map(columns.map(col => [col.key, col]))
    return (key: string, direction: SortDirection = 'asc'): ((a: T, b: T) => number) | undefined => {
      const custom = getSortComparator?.(key)
      if (custom) return custom
      const accessor = byKey.get(key)?.accessor
      if (!accessor) return undefined
      const missingRank = direction === 'desc' ? -1 : 1
      return (a: T, b: T) => compareRawValues(accessor(a), accessor(b), missingRank)
    }
  }, [columns, getSortComparator])

  // Apply sorting
  const sortedData = useMemo(() => {
    if (!sortConfig) return data
    const comparator = comparatorFor(sortConfig.key, sortConfig.direction)
    if (!comparator) return data
    return sortData(data, sortConfig, () => comparator)
  }, [data, sortConfig, comparatorFor])

  // The page actually rendered, clamped rather than stored. Callers filter before handing `data`
  // over, so the set can shrink under the current page between renders; clamping lands on a page
  // that exists instead of on an empty one. Correcting it afterwards with a state write would mean
  // catching every path that can shrink the data, and the one that gets missed shows a populated
  // grid with no rows in it.
  const totalPages = pagination ? Math.max(1, Math.ceil(sortedData.length / pageSize)) : 1
  const currentPage = Math.min(page, totalPages)

  const paginatedData = useMemo(() => {
    if (!pagination) {
      return sortedData
    }
    const start = (currentPage - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, currentPage, pageSize, pagination])

  // A different set of rows is a different list, and page 3 of it is not where anyone means to
  // land. Row count catches most of it; `pageResetKey` catches the rest, where a caller's new
  // filter happens to match the same number of rows as the old one.
  useEffect(() => {
    setPage(1)
  }, [data.length, pageResetKey])

  const tableClass = `${scrollable ? 'w-max min-w-full' : 'min-w-full'} divide-y divide-slate-200 dark:divide-slate-700`

  // A header only offers sorting when the grid can actually sort — otherwise the reader gets a pointer,
  // a hover state and a flipping arrow over a list that never moves (review: Copilot, ORB-706).
  //
  // ORB-715 answered that by making the capability derivable instead of declarable: a sortable column
  // with an accessor sorts, with no comparator prop at all. The guard stays for the residual case — a
  // column marked sortable with neither an accessor nor a comparator case.
  const canSort = (col: ColumnConfig<T>) => !!col.sortable && !!comparatorFor(col.key)

  const ariaSortFor = (col: ColumnConfig<T>): 'ascending' | 'descending' | 'none' | undefined => {
    if (!canSort(col)) return undefined
    const direction = getSortIndicator(col.key)
    return direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'
  }

  // Render sort indicator
  const renderSortIcon = (columnKey: string) => {
    const direction = getSortIndicator(columnKey)
    if (direction === 'asc') {
      return <ArrowUp size={14} className="text-primary-600 dark:text-primary-400" />
    }
    if (direction === 'desc') {
      return <ArrowDown size={14} className="text-primary-600 dark:text-primary-400" />
    }
    return <ArrowUpDown size={14} className="text-slate-400 dark:text-slate-500" />
  }

  // Row keyboard handler for accessibility
  const handleRowKeyDown = (e: KeyboardEvent, item: T) => {
    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onRowClick(item)
    }
  }

  // Export handler
  const handleExport = exportConfig ? () => {
    if (sortedData.length === 0) return
    const csv = generateCsv(sortedData, exportConfig.columns)
    downloadCsv(csv, exportConfig.getFilename())
  } : undefined

  // The header strip. Every grid has one, because every grid has a title.
  //
  // Two cells and one rule: the left says what the rows ARE, the right holds the things that ACT on
  // them. The count belongs on the left — it is a statement about the set, not a control over it — and
  // keeping it there is what leaves the right-hand group uniform across every grid in the app.
  //
  // Wrapping is deliberate. The strip cannot shrink below the search box's intrinsic width, and the
  // card clips (`overflow-hidden`), so an unwrapped strip loses its last button off the right edge on a
  // phone with no scrollbar to reach it. Wrapped, the whole control cluster drops to a second line.
  const count = resultCount ?? data.length
  const columnToolbar = (
    <div className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
      <div className="flex min-w-0 items-baseline gap-2">
        <h3
          id={titleId}
          title={typeof title === 'string' ? title : undefined}
          className="truncate text-sm font-semibold text-slate-900 dark:text-white"
        >
          {title}
        </h3>
        {/* Suppressed while loading: a derived count beside a spinner reads "0 results", which is the
            one value that is never true. Gated here rather than at every call site. */}
        {!isLoading && (
          <span
            className="shrink-0 tabular-nums text-xs text-slate-500 dark:text-slate-400"
            // Announced only when there is a search box, where the count is the feedback for what the
            // reader just typed. Without one it would fire on unrelated re-renders.
            aria-live={search ? 'polite' : undefined}
            aria-atomic="true"
          >
            {count.toLocaleString()} {count === 1 ? 'result' : 'results'}
          </span>
        )}
        {/* Hidden below `sm`: two truncating siblings share the shrink, so on a phone a long title
            leaves the subtitle as "Onl…" — worse than not showing it at all. */}
        {subtitle !== undefined && (
          <span className="hidden min-w-0 truncate text-xs text-slate-500 dark:text-slate-400 sm:inline">
            {subtitle}
          </span>
        )}
      </div>
      <div className="ml-auto flex w-full items-center gap-2 sm:w-auto">
        {search && (
          <div className="relative min-w-0 flex-1 sm:w-56 sm:flex-none">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
            />
            <input
              type="search"
              value={search.value}
              onInput={e => search.onChange((e.target as HTMLInputElement).value)}
              placeholder={search.placeholder ?? 'Search'}
              aria-label={search.ariaLabel ?? search.placeholder ?? 'Search'}
              className={`w-full rounded-md border py-1.5 pl-8 pr-2.5 text-sm focus:outline-none focus:ring-2 ${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.INPUT.PLACEHOLDER} ${BRAND_CLASSES.FOCUS.PRIMARY} ${BRAND_CLASSES.FOCUS.BORDER}`}
            />
          </div>
        )}
        {toolbarActions}
        {exportConfig && (
          // `aria-disabled`, not `disabled`: a disabled button leaves the tab order and most browsers
          // suppress its tooltip, so the one control that could explain why it does nothing goes
          // silent. This one stays focusable and says so.
          <button
            onClick={() => sortedData.length > 0 && handleExport?.()}
            aria-disabled={sortedData.length === 0}
            className={`${TOOLBAR_BUTTON} ${
              sortedData.length === 0 ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-50 dark:hover:bg-slate-600'
            }`}
            title={sortedData.length === 0 ? 'Nothing to export' : 'Export to CSV'}
          >
            <Download size={14} />
            Export
          </button>
        )}
        {columnVisibilityKey && (
          <button
            onClick={() => setIsColumnSelectorOpen(true)}
            className={`${TOOLBAR_BUTTON} hover:bg-slate-50 dark:hover:bg-slate-600`}
            title="Select Columns"
          >
            <Columns size={14} />
            Columns
          </button>
        )}
      </div>
    </div>
  )

  // Column selector modal
  const columnSelectorModal = columnVisibilityKey ? (
    <ColumnSelectorModal
      isOpen={isColumnSelectorOpen}
      onClose={() => setIsColumnSelectorOpen(false)}
      columns={selectorItems}
      onToggle={toggleColumn}
      onReset={resetToDefaults}
    />
  ) : null

  // ONE structure for every state (ORB-715).
  //
  // Loading, empty and populated used to be three separate returns, and the populated one sat a div
  // deeper than the other two so it could hang the pager beside the card. That was survivable while
  // the strip held only buttons. It stopped being survivable the moment the search box moved into it:
  // Preact diffs unkeyed children by position, so crossing `data.length === 0` — which is exactly what
  // typing into that box does — mapped the wrapper onto the card, the card onto the strip, and tore
  // down the input. The reader lost focus mid-word, on the one control that could undo the search.
  //
  // So the card, the strip, the header row and the pager render once, in one place, and only the body
  // of the table changes. There is nothing left to keep in sync.
  const body = isLoading ? (
    <tr>
      <td colSpan={visibleColumns.length} className="px-4 py-12 text-center">
        <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600"></div>
          <span>Loading...</span>
        </div>
      </td>
    </tr>
  ) : data.length === 0 ? (
    <tr>
      <td colSpan={visibleColumns.length} className="px-4 py-12 text-center text-slate-500 dark:text-slate-400">
        {emptyMessage}
      </td>
    </tr>
  ) : (
    paginatedData.map((item, index) => (
      <tr
        key={getRowKey(item)}
        tabIndex={onRowClick ? 0 : undefined}
        className={
          onRowClick
            ? 'hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500'
            : ''
        }
        onClick={() => onRowClick?.(item)}
        onKeyDown={(e) => handleRowKeyDown(e, item)}
      >
        {visibleColumns.map((col) => (
          <td
            key={col.key}
            className={`px-4 py-3 text-sm text-slate-900 dark:text-slate-100 ${col.className || ''} ${col.hidden || ''}`}
            style={{ textAlign: col.align || 'left' }}
          >
            {col.render ? col.render(item, index) : col.accessor ? col.accessor(item) : null}
          </td>
        ))}
      </tr>
    ))
  )

  return (
    <>
      <div>
        <div className={CARD_CLASS}>
          {columnToolbar}
          <div className="overflow-x-auto">
            <table className={tableClass} aria-labelledby={titleId}>
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  {visibleColumns.map((col) => (
                    // A sortable header is a real <button> inside the <th>, not a click handler on the
                    // cell (ORB-706 review, deferred here). A <th> is not focusable and answers no key,
                    // so the sort was mouse-only on every grid in the app. `aria-sort` on the cell is
                    // what tells a screen reader which column the order is currently coming from.
                    <th
                      key={col.key}
                      style={{ width: col.width }}
                      scope="col"
                      aria-sort={ariaSortFor(col)}
                      className={`px-4 py-3 text-${col.align || 'left'} text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider ${col.hidden || ''}`}
                    >
                      {canSort(col) && data.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => requestSort(col.key)}
                          className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-2 rounded px-1 py-0.5 text-left uppercase tracking-wider select-none hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-slate-200"
                        >
                          <span>{col.header}</span>
                          {renderSortIcon(col.key)}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>{col.header}</span>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">{body}</tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 mt-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
            {/* The header owns the total. This said "of N items" as well, so a paged card stated the
                same number twice, ~400px apart, in two different formats. */}
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, sortedData.length)}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      {columnSelectorModal}
    </>
  )
}
